import { envConfig } from '#constants/envConfig';

/**
 * Firebase App Check для нативной оболочки: токен, которым платформа ручается, что запрос идёт
 * из подлинной сборки нашего приложения (на Android за этим стоит Play Integrity).
 *
 * Зачем он вместо капчи. reCAPTCHA v3 в WebView не работает в принципе: страница открыта с
 * `https://localhost`, истории браузера и репутации домена нет, и живому человеку стабильно
 * выдаётся score 0.1. 18.08.2026 из-за этого регистрация из приложения не работала ни у кого —
 * один пользователь нажал «Зарегистрироваться» восемь раз и так и не завёл аккаунт. Шлюз
 * принимает этот токен заголовком `tm-app-check` и в таком случае капчу не спрашивает вовсе.
 *
 * Всё внутри закрыто `envConfig.IS_NATIVE` — константой времени сборки. В веб-сборке она
 * сворачивается в `false`, динамический импорт Capacitor вырезается, и веб-бандл о нём не знает.
 */

type AppCheckPlugin = {
  getToken(): Promise<{ token: string | null }>;
};

// undefined = плагин ещё не запрашивали; null = запрашивали, его нет (веб или старая сборка).
let cached: AppCheckPlugin | null | undefined;
let loading: Promise<void> | null = null;

/**
 * Загружает прокси плагина ровно один раз и кладёт в `cached`. Возвращает `Promise<void>` и
 * НИКОГДА сам прокси: `registerPlugin` отдаёт Proxy, который перехватывает любое обращение к
 * свойству, включая `.then`. Стоит такому прокси стать значением, которым резолвится промис, —
 * рантайм примет его за thenable, вызовет несуществующий нативный метод `then` и уронит вызов
 * ещё до моста (ровно эта ловушка описана в `deviceSettings.ts`).
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        cached = registerPlugin<AppCheckPlugin>('AppCheck');
      } catch {
        cached = null;
      }
    })();
  }
  return loading;
}

/**
 * @returns токен App Check либо `null` — в вебе, в сборке без плагина и на устройстве, где
 * Play Integrity недоступен. `null` не ошибка: шлюз в этом случае просто спросит капчу.
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }

  await ensurePlugin();
  if (!cached) {
    return null;
  }

  try {
    const { token } = await cached.getToken();
    return token ?? null;
  } catch {
    // Молчим намеренно: отсутствие токена — рабочий сценарий, а не сбой, о котором стоит
    // сообщать в Sentry на каждый запрос.
    return null;
  }
}
